/**
 * ShareProfileModal Component
 *
 * Modal for sharing: the public profile link with canned snippets, and
 * scoped private share links that show only the items you pick.
 */

import { useState } from 'react';
import clsx from 'clsx';
import { ShareLinksManager } from './ShareLinksManager';

export interface ShareProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  publicSlug: string;
  displayName?: string;
}

const SHARE_SNIPPETS = [
  {
    id: 'linkedin',
    label: 'LinkedIn',
    icon: '💼',
    template: (name: string, url: string) =>
      `Check out ${name}'s speaker profile on BeHeard! ${url}`,
  },
  {
    id: 'twitter',
    label: 'Twitter/X',
    icon: '🐦',
    template: (name: string, url: string) =>
      `${name} is crushing it as a speaker! Check out their profile: ${url} #PublicSpeaking #BeHeard`,
  },
  {
    id: 'email',
    label: 'Email',
    icon: '✉️',
    template: (name: string, url: string) =>
      `Hi!\n\nI wanted to share ${name}'s speaker profile with you. They've been practicing and improving their public speaking skills on BeHeard.\n\nCheck it out: ${url}\n\nBest regards`,
  },
  {
    id: 'generic',
    label: 'Generic',
    icon: '📋',
    template: (name: string, url: string) =>
      `${name}'s BeHeard Speaker Profile: ${url}`,
  },
];

export function ShareProfileModal({
  isOpen,
  onClose,
  publicSlug,
  displayName = 'My',
}: ShareProfileModalProps) {
  const [copied, setCopied] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState<string | null>(null);

  const shareUrl = `${window.location.origin}/u/${publicSlug}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const handleCopySnippet = async (snippetId: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSnippet(snippetId);
      setTimeout(() => setCopiedSnippet(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Share Your Profile
          </h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-5 space-y-6 overflow-y-auto max-h-[calc(90vh-130px)]">
          {/* Profile URL Section */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Your Public Profile URL
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-300 rounded-lg text-gray-700 text-sm focus:outline-none"
              />
              <button
                onClick={handleCopyLink}
                className={clsx(
                  'px-4 py-2.5 rounded-lg font-medium text-sm transition-all min-w-[100px]',
                  copied
                    ? 'bg-green-500 text-white'
                    : 'bg-primary-600 text-white hover:bg-primary-700'
                )}
              >
                {copied ? '✓ Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-3 bg-white text-gray-500">
                or use a snippet
              </span>
            </div>
          </div>

          {/* Canned Snippets */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">
              Ready-to-Share Snippets
            </label>
            <div className="space-y-3">
              {SHARE_SNIPPETS.map((snippet) => {
                const text = snippet.template(displayName, shareUrl);
                const isCopied = copiedSnippet === snippet.id;

                return (
                  <div
                    key={snippet.id}
                    className="border border-gray-200 rounded-lg overflow-hidden hover:border-gray-300 transition-colors"
                  >
                    <div className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
                      <span className="text-sm font-medium text-gray-700">
                        {snippet.icon} {snippet.label}
                      </span>
                      <button
                        onClick={() => handleCopySnippet(snippet.id, text)}
                        className={clsx(
                          'px-3 py-1 text-xs font-medium rounded-md transition-all',
                          isCopied
                            ? 'bg-green-100 text-green-700'
                            : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                        )}
                      >
                        {isCopied ? '✓ Copied' : 'Copy'}
                      </button>
                    </div>
                    <div className="px-4 py-3">
                      <p className="text-sm text-gray-600 whitespace-pre-wrap">
                        {text}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Scoped share links: only chosen items, revocable, work while private */}
          <div className="border-t border-gray-200 pt-5">
            <ShareLinksManager />
          </div>

          {/* Tips */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg px-4 py-3">
            <p className="text-sm text-blue-800">
              <span className="font-medium">💡 Tip:</span> Share your profile
              after completing practice sessions to showcase your improvement
              journey!
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="w-full px-4 py-2.5 text-gray-700 bg-white border border-gray-300 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
